
import type { Flashcard, FlashcardDeck } from "@/types/flashcard";

/**
 * Transform database flashcard object to match TypeScript interface
 */
export function transformDatabaseFlashcard(dbFlashcard: any): Flashcard {
  if (!dbFlashcard) {
    console.warn('Received null or undefined flashcard data');
    return {
      id: '',
      front: '',
      back: '',
      category: '',
      subject: '',
      difficulty: 'medium',
      repetition_count: 0,
      confidence: 0,
      deck_id: ''
    };
  }
  
  return {
    id: dbFlashcard.id || '',
    front: dbFlashcard.front || '',
    back: dbFlashcard.back || '',
    category: dbFlashcard.category || '',
    subject: dbFlashcard.subject || '',
    last_reviewed: dbFlashcard.last_reviewed,
    next_review: dbFlashcard.next_review,
    difficulty: dbFlashcard.difficulty || 'medium',
    repetition_count: typeof dbFlashcard.repetition_count === 'number' ? dbFlashcard.repetition_count : 0,
    confidence: typeof dbFlashcard.confidence === 'number' ? dbFlashcard.confidence : 0,
    deck_id: dbFlashcard.deck_id || ''
  };
}

/**
 * Transform database flashcard deck object to match TypeScript interface
 */
export function transformDatabaseFlashcardDeck(dbDeck: any): FlashcardDeck {
  if (!dbDeck) {
    console.warn('Received null or undefined deck data');
    return {
      id: '',
      name: '',
      description: '',
      category: '',
      subject: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      card_count: 0
    };
  }
  
  return {
    id: dbDeck.id || '',
    name: dbDeck.name || '',
    description: dbDeck.description || '',
    category: dbDeck.category || '',
    subject: dbDeck.subject || '',
    created_at: dbDeck.created_at || new Date().toISOString(),
    updated_at: dbDeck.updated_at || new Date().toISOString(),
    card_count: typeof dbDeck.card_count === 'number' ? dbDeck.card_count : 0
  };
}

/**
 * Transform TypeScript interface flashcard to database object
 */
export function transformFlashcardToDatabase(flashcard: Partial<Flashcard>): Record<string, any> {
  const dbObject: Record<string, any> = {};
  
  // Only include properties that are defined
  if (flashcard.id !== undefined) dbObject.id = flashcard.id;
  if (flashcard.front !== undefined) dbObject.front = flashcard.front;
  if (flashcard.back !== undefined) dbObject.back = flashcard.back;
  if (flashcard.category !== undefined) dbObject.category = flashcard.category;
  if (flashcard.subject !== undefined) dbObject.subject = flashcard.subject;
  if (flashcard.last_reviewed !== undefined) dbObject.last_reviewed = flashcard.last_reviewed;
  if (flashcard.next_review !== undefined) dbObject.next_review = flashcard.next_review;
  if (flashcard.difficulty !== undefined) dbObject.difficulty = flashcard.difficulty;
  if (flashcard.repetition_count !== undefined) dbObject.repetition_count = flashcard.repetition_count;
  if (flashcard.confidence !== undefined) dbObject.confidence = flashcard.confidence;
  if (flashcard.deck_id !== undefined) dbObject.deck_id = flashcard.deck_id;
  
  return dbObject;
}

/**
 * Check if the given text contains "mistakes" (case insensitive)
 */
export function isMistakeDeck(deckName: string): boolean {
  return deckName.toLowerCase().includes('mistake');
}

/**
 * Format relative time for next review
 */
export function formatNextReview(nextReviewDate: string | undefined): string {
  if (!nextReviewDate) return 'Not scheduled';
  
  const now = new Date();
  const next = new Date(nextReviewDate);
  const diffDays = Math.floor((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'Due now';
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays < 7) return `Due in ${diffDays} days`;
  if (diffDays < 30) return `Due in ${Math.floor(diffDays/7)} weeks`;
  return `Due in ${Math.floor(diffDays/30)} months`;
}
